import { useEffect, useState, FunctionComponent } from 'react';
import { IconCaretLeft, IconEmail, IconPhone } from '@lmig/lmds-react-icons';
import { Link } from '@lmig/lmds-react-link';
import { Heading } from '@lmig/lmds-react-typography';
import { formatPhone } from 'Utilities/StringFormatter';
import { IKeyContact } from './KeyContactsPanel';
import SitecoreLink from 'Foundation/GlassMapper/SitecoreLink';

interface IContactDetailSubPanel {
  activeKeyContact: IKeyContact;
  OnClick(contact: IKeyContact | undefined): void;
}

const ContactDetailSubPanel: FunctionComponent<IContactDetailSubPanel> = (props: IContactDetailSubPanel) => {
  const [isStaticContact, setIsStaticContact] = useState<boolean>(false);

  useEffect(() => {
    if (!props.activeKeyContact) setIsStaticContact(false);
    else setIsStaticContact(props.activeKeyContact.isStaticContact);
  }, [props.activeKeyContact]);

  const getInitials = () =>
    props.activeKeyContact &&
    !props.activeKeyContact.isStaticContact &&
    props.activeKeyContact.FirstName?.length &&
    props.activeKeyContact.LastName?.length
      ? `${props.activeKeyContact.FirstName[0]}${props.activeKeyContact.LastName[0]}`
      : props.activeKeyContact && props.activeKeyContact.isStaticContact && props.activeKeyContact.Title?.length
      ? `${props.activeKeyContact.Title[0]}`
      : null;

  const hasValue = (value: string | undefined) => value && value.length > 0 && value !== 'Not Available';

  function hasValidPhoneTypes() {
    return (
      hasValue(props.activeKeyContact.DirectDial) ||
      hasValue(props.activeKeyContact.Mobile) ||
      hasValue(props.activeKeyContact.Fax) ||
      hasValue(props.activeKeyContact.TollFree)
    );
  }

  return (
    <>
      <div>
        <div onClick={() => props.OnClick(undefined)} className="key-contact-breadcrumb">
          <IconCaretLeft color="teal" size="16" /> Contacts
        </div>
        <div>
          {/* HEADING SECTIONS */}
          {!isStaticContact && (
            <div className="key-contact-name-row">
              {getInitials() && <div className="key-contact-initials-circle">{getInitials()}</div>}
              <div>
                <Heading type="h4">
                  {props.activeKeyContact.FirstName} {props.activeKeyContact.LastName}
                </Heading>
                <Heading type="h4-light" className="key-contact-role">
                  {props.activeKeyContact.PortalRoleName}
                </Heading>
              </div>
            </div>
          )}
          {isStaticContact && (
            <>
              {hasValue(props.activeKeyContact.Title) && (
                <div className="key-contact-name-row">
                  {getInitials() && <div className="key-contact-initials-circle">{getInitials()}</div>}
                  <div>
                    <Heading type="h4">{props.activeKeyContact.Title}</Heading>
                  </div>
                </div>
              )}
              {hasValue(props.activeKeyContact.Department) && (
                <div className="key-contact-row">
                  <span>
                    <div className="key-contact-label">Department</div>
                    <Heading type="h4-light" className="key-contact-role">
                      {props.activeKeyContact.Department}
                    </Heading>
                  </span>
                </div>
              )}
            </>
          )}
          {/* PHONE SECTIONS */}
          <div className="key-contact-phone-row">
            {hasValidPhoneTypes() && <IconPhone size="16" />}
            <div>
              {!isStaticContact && (
                <>
                  {hasValue(props.activeKeyContact.DirectDial) && (
                    <div className="key-contact-row">
                      <span>
                        <div className="key-contact-label">Direct</div>
                        <Link
                          href={`tel:${props.activeKeyContact.DirectDial}`}
                          variant="standalone"
                          caretPosition="none"
                        >
                          {formatPhone(props.activeKeyContact.DirectDial)?.toLowerCase()}
                        </Link>
                      </span>
                    </div>
                  )}
                </>
              )}
              {hasValue(props.activeKeyContact.Mobile) && (
                <div className="key-contact-row">
                  <span>
                    <div className="key-contact-label">Phone</div>
                    <Link href={`tel:${props.activeKeyContact.Mobile}`} variant="standalone" caretPosition="none">
                      {formatPhone(props.activeKeyContact.Mobile)?.toLowerCase()}
                    </Link>
                  </span>
                </div>
              )}
              {hasValue(props.activeKeyContact.Fax) && (
                <div className="key-contact-row">
                  <span>
                    <div className="key-contact-label">Fax</div>
                    <Link href={`tel:${props.activeKeyContact.Fax}`} variant="standalone" caretPosition="none">
                      {formatPhone(props.activeKeyContact.Fax)?.toLowerCase()}
                    </Link>
                  </span>
                </div>
              )}
              {!isStaticContact && (
                <>
                  {hasValue(props.activeKeyContact.TollFree) && (
                    <div className="key-contact-row">
                      <span>
                        <div className="key-contact-label">Toll free</div>
                        <Link href={`tel:${props.activeKeyContact.TollFree}`} variant="standalone" caretPosition="none">
                          {formatPhone(props.activeKeyContact.TollFree)?.toLowerCase()}
                        </Link>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* EMAIL SECTIONS */}
          {props.activeKeyContact.Email && props.activeKeyContact.Email !== 'Not Available' && (
            <div className="key-contact-row">
              <IconEmail size="16" />
              <span>
                <div className="key-contact-label">Email</div>
                <Link
                  href={`mailto:${props.activeKeyContact.Email}`}
                  variant="standalone"
                  caretPosition="none"
                  className="linkwrap"
                >
                  {props.activeKeyContact.Email?.toLowerCase()}
                </Link>
              </span>
            </div>
          )}
          {/* LINK SECTIONS */}
          {props.activeKeyContact.Link?.Url && (
            <div className="key-contact-row">
              <SitecoreLink
                Class={props.activeKeyContact.Link.Class}
                Goal={props.activeKeyContact.Link.Goal}
                key={props.activeKeyContact.Link.Text}
                Target={props.activeKeyContact.Link.Target}
                Text={props.activeKeyContact.Link.Text ?? ''}
                Url={props.activeKeyContact.Link.Url}
                CaretPosition={props.activeKeyContact.Link.CaretPosition}
                Favoritable={false}
                IsKeyContactLink={false}
              />
            </div>
          )}
          {/* OTHER / SUBTEXT SECTIONS */}
          {props.activeKeyContact.Link?.SubText && (
            <div className="key-contact-row">
              <p>{props.activeKeyContact.Link?.SubText}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactDetailSubPanel;
